import { useMemo, useState } from "react";
import { useMyCompanies, useGlobalCompanies, useCompanyFacets } from "@/hooks/useCompanies";
import { cn } from "@/cn";
import {
  CompanyDirectory,
  globalToListItem,
  mergedToListItem,
} from "@/components/companies/company-directory";

type Tab = "mine" | "directory";

export function CompaniesPage() {
  const [tab, setTab] = useState<Tab>("mine");
  const [segmentIndustry, setSegmentIndustry] = useState("");
  const [segmentCountry, setSegmentCountry] = useState("");

  const globalFilters = useMemo(
    () => ({
      industry: segmentIndustry || undefined,
      country: segmentCountry || undefined,
    }),
    [segmentIndustry, segmentCountry],
  );

  const { data: myData, isLoading: myLoading } = useMyCompanies();
  const { data: globalData, isLoading: globalLoading } =
    useGlobalCompanies(globalFilters);
  const { data: facets } = useCompanyFacets();

  const myCompanies = myData?.companies ?? [];
  const globalCompanies = globalData?.companies ?? [];
  const isLoading = tab === "mine" ? myLoading : globalLoading;
  const hasDirectoryFilters = Boolean(segmentIndustry || segmentCountry);

  const myItems = useMemo(
    () => myCompanies.map(mergedToListItem),
    [myCompanies],
  );
  const globalItems = useMemo(
    () => globalCompanies.map(globalToListItem),
    [globalCompanies],
  );

  return (
    <>
      <header className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Employers</h1>
            <p className="text-muted-foreground">
              Research employers, enable ATS scans, and open Find Jobs from any profile.
            </p>
          </div>
          <div className="flex gap-1 rounded-lg border border-border p-1">
            {(
              [
                { id: "mine" as const, label: `My employers (${myCompanies.length})` },
                { id: "directory" as const, label: "Directory" },
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition",
                  tab === t.id
                    ? "bg-brand text-brand-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {!isLoading && tab === "mine" && myCompanies.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No employers tracked yet. They appear when you add jobs, enable scan, or import from Find Jobs.
          </p>
        </div>
      )}

      {!isLoading &&
        tab === "directory" &&
        globalItems.length === 0 &&
        !hasDirectoryFilters && (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <p className="text-sm text-muted-foreground">
              No companies in the directory yet. Run the seed-companies script to populate tier-1 employers.
            </p>
          </div>
        )}

      {!isLoading && tab === "mine" && myItems.length > 0 && (
        <CompanyDirectory
          items={myItems}
          variant="mine"
          emptyMessage="No companies match your search."
        />
      )}

      {!isLoading &&
        tab === "directory" &&
        (globalItems.length > 0 || hasDirectoryFilters) && (
          <CompanyDirectory
            items={globalItems}
            variant="directory"
            facets={facets}
            industry={segmentIndustry}
            country={segmentCountry}
            onIndustryChange={setSegmentIndustry}
            onCountryChange={setSegmentCountry}
            emptyMessage="No companies match your filters."
          />
        )}
    </>
  );
}
